/**
 * Where the public checkout reads its pay token from — and, more importantly,
 * where it refuses to.
 *
 * ## The token is a bearer credential
 *
 * The public pay surface has no session: possession of the `payToken` *is* the
 * authorisation (`stablecoin-invoicing-spec.md`: "authorised by the possession
 * of the pay token, not by RBAC"). It is unguessable, stored as a hash by the
 * service, and revocable by rotation. Three rules follow, and each one is
 * asserted by the unit suite rather than trusted to review:
 *
 *   1. **Never in a query string.** A query string is written to proxy logs,
 *      browser history and `Referer` headers on any outbound navigation. The
 *      design's link shape is a path segment (`/pay/<token>`, spec line 69);
 *      a token found in `?token=` is therefore **ignored, not honoured** —
 *      accepting it would make the leak-bearing form work, which is the one
 *      way a payer would ever be handed one.
 *   2. **Never logged.** Nothing in this module (or in `pay-api.ts`) writes the
 *      token to the console, to an error message, or into a thrown `Error`. A
 *      transport failure reports its own code; the request path is never
 *      attached to a user-visible or logged string.
 *   3. **Not left in the address bar when it arrived in the fragment.** The
 *      fragment form is supported for links minted before the route accepts a
 *      path segment; the fragment is stripped with `replaceState` on the first
 *      read, so an in-page navigation cannot carry it onward.
 *
 * ## Malformed means not-active
 *
 * An absent, non-URL-safe, or over-long token is refused *locally*: the page
 * renders the not-active state and makes no request at all. That is both the
 * contract's rule ("unknown, revoked and malformed tokens all show the
 * identical page") and the cheapest way to avoid leaking token existence — a
 * request that is never sent cannot be probed.
 */

/**
 * Longest token this page will ever put in a request path. A token is
 * unguessable and therefore short; anything past this is a hostile or broken
 * link, and refusing locally keeps a megabyte of path out of the request line
 * and out of any log that records one.
 */
export const MAX_PAY_TOKEN_LENGTH = 256;

/**
 * The characters a pay token may contain, which is the URL-safe set plus the
 * standard-base64 two so a contract that mints `+` or `=` is not refused. `/`
 * is deliberately absent: it would split the path segment and change which
 * route the request lands on. Anything outside this set — a space, a `%`, a
 * quote, a control character — is a malformed link.
 */
const TOKEN_PATTERN = /^[A-Za-z0-9._~+=-]+$/;

/** True when a candidate token is safe to place in a request path. */
export function isWellFormedPayToken(value: unknown): value is string {
  if (typeof value !== "string") return false;
  if (value.length === 0 || value.length > MAX_PAY_TOKEN_LENGTH) return false;
  if (value === "." || value === "..") return false;
  return TOKEN_PATTERN.test(value);
}

export interface PayLinkToken {
  token: string;
  /** Where it came from — for the tests and the page's own notes, never logged. */
  source: "path" | "fragment";
}

/** Percent-decodes without throwing; a broken escape sequence is malformed. */
function decode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * Reads the pay token from the current URL.
 *
 * Order is deliberate: the **path segment** first, because that is the shape
 * the design mints (`/pay/<unguessable token>`, spec line 69 and G15 — "the
 * invoice number is never part of the link"). The **fragment** (`#token=…`, or
 * a bare `#<token>`) is second, and exists for links minted before the route
 * accepts a segment; it is consumed once and removed.
 *
 * Anything else — no token, a token in the query string, a token that is not
 * URL-safe, a token longer than `MAX_PAY_TOKEN_LENGTH` — returns `null`, and
 * the caller renders the not-active state without calling the service.
 */
export function readPayToken(
  location: Location = window.location,
  history: History = window.history,
): PayLinkToken | null {
  const { pathname, hash } = location;

  // 1. Path segment: everything after the last "/pay/", up to the next slash.
  const marker = "/pay/";
  const at = pathname.lastIndexOf(marker);
  if (at >= 0) {
    const rest = pathname.slice(at + marker.length);
    const segment = rest.split("/")[0];
    const decoded = decode(segment);
    if (decoded !== null && isWellFormedPayToken(decoded)) return { token: decoded, source: "path" };
    // A path that looks like a link but carries nothing usable is a malformed
    // link: fall through to null rather than trying the fragment, so a crafted
    // URL cannot mix a bad segment with a good fragment.
    return null;
  }

  // 2. Fragment. Consumed once, then removed from the address bar.
  if (!hash || hash.length < 2) return null;
  const raw = hash.slice(1);
  const params = new URLSearchParams(raw);
  const candidate = params.get("token") ?? (params.has("token") ? "" : raw.split("&")[0]);

  // Strip the fragment before validating, so even a malformed token does not
  // sit in the address bar after the page has read it.
  stripFragment(location, history);

  const decoded = decode(candidate);
  if (decoded === null || !isWellFormedPayToken(decoded)) return null;
  return { token: decoded, source: "fragment" };
}

/** Removes the fragment, leaving path and query untouched. Never adds a value. */
function stripFragment(location: Location, history: History): void {
  const clean = `${location.pathname}${location.search}`;
  try {
    history.replaceState(null, "", clean);
  } catch {
    // A sandboxed document can refuse `replaceState`. The token has still been
    // read into memory only; the page keeps working and the refusal is silent
    // because there is nothing safe to report to the payer about a URL.
  }
}
